// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridSeparator.jsx" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import {
  forEach,
  concat,
  get,
  head,
  isEmpty,
  isNil,
  map,
  omit,
  sortBy,
  take,
  flattenDeep,
  getOr,
  flow
} from 'lodash/fp';

import CollapsibleList from 'share/components/controls/Izenda.CollapsibleList';
import ReportPartTitle from '../FieldContainers/Izenda.ReportPartTitle';
import ProcessDataUtils from 'share/utils/Izenda.ProcessDataUtils';
import ReportPartGridConstants from 'logic/data/Izenda.ReportPartGridConstants';
import SubReportLink from './Izenda.SubReportLink';
import ReportPartGridStylingUtil from './Izenda.ReportPartGridStylingUtil';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ReportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import AppGlobal from 'Izenda.AppGlobal';
import { GRID_STYLES } from 'logic/data/Izenda.ReportPartGridConstants';

const REPORT_PART_MODE = ReportBodyConstants.REPORT_PART_MODE;
const COLLAPSE_STATUS = ReportPartGridConstants.COLLAPSE_STATUS;

/**
 * ReportPartGridSeparator component
 */
const SeparatorItem = observer(props => {
  const {
    separatorContent,
    separatorStyle,
    reportPart,
    staticRender,
    getFilterInfoForSubReport,
    renderListeners,
    isLogicalSeparator,
    isHeaderOnly
  } = props;

  if (isLogicalSeparator) {
    return (
      <SeparatorBody {...props} staticRender={staticRender}/>
    );
  }

  return (
    <div>
      {!isHeaderOnly && (
        <SeparatorHeader
          separatorStyle={separatorStyle}
          keyCollection={separatorContent.key}
          separatorContent={separatorContent}
          reportPart={reportPart}
          getFilterInfoForSubReport={getFilterInfoForSubReport}
          renderListeners={renderListeners}
        />
      )}
      <SeparatorBody {...props} staticRender={staticRender}/>
    </div>
  );
});

const CollapsibleHeader = observer(props => {
  return (
    <section>
      <i className="icon-toggle-triangle collapse-icon" onClick={props.toggleCollapse}/>
      <SeparatorHeader
        separatorStyle={props.separatorStyle}
        keyCollection={props.keyCollection}
        separatorContent={props.separatorContent}
        toggleCollapse={props.toggleCollapse}
        getFilterInfoForSubReport={props.getFilterInfoForSubReport}
        reportPart={props.reportPart}
      />
    </section>
  );
});

@observer
class SeparatorBody extends Component {
  renderListeners = [];

  componentWillMount() {
    this.renderListeners = [];
  }

  componentDidMount() {
    const {props} = this;
    const {printingMode, finishedRender} = props;

    printingMode && PrintingUtils.publishFinishedRender(this.renderListeners, function () {
      finishedRender && finishedRender();
    });
  }

  render() {
    const {props, renderListeners} = this;
    const {
      keyPath,
      separatorContent,
      mainComponent,
      staticRender,
      isLogicalSeparator,
      isHeaderOnly
    } = props;

    const GridComponent = mainComponent;
    const rest = omit(['finishedRender'], props);

    let subKeyPath;
    if (isLogicalSeparator) {
      subKeyPath = separatorContent.length && separatorContent[0].key;
    } else {
      const currentKeyPath = map(item => ({
        element: item.element,
        value: item.value
      }), separatorContent.key);
      subKeyPath = concat(keyPath || [], currentKeyPath);
    }

    // Render subgroup
    if (!isEmpty(separatorContent.subGroup)) {
      return (
        <div>
          {map(item => {
            const key = map('value', item.key).join('_');
            PrintingUtils.pushRenderListener(renderListeners, key);
            return (
              <SeparatorItem
                {...rest}
                separatorContent={item}
                key={key}
                keyPath={subKeyPath}
                staticRender={staticRender}
                finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, key)}
                isLogicalSeparator={isLogicalSeparator}
                isHeaderOnly={isHeaderOnly}
              />
            );
          }, separatorContent.subGroup)}
        </div>
      );
    }

    const keyString = JSON.stringify(subKeyPath);
    const separatorBodyKey = `SeparatorBody.${keyString}`;
    PrintingUtils.pushRenderListener(renderListeners, separatorBodyKey);
    const records = separatorContent.data || separatorContent;

    // Add grid detail here
    return (
      <GridComponent
        {...rest}
        records={records}
        keyPath={subKeyPath}
        staticRender={staticRender}
        finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, separatorBodyKey)}
        isLogicalSeparator={isLogicalSeparator}
        isHeaderOnly={isHeaderOnly}
      />
    );
  }
}

@observer
class SeparatorHeaderItem extends Component {

  componentDidMount() {
    const {props} = this;
    const {printingMode, finishedRender, item: {element: {numberOfPointActions}}} = props;
    !numberOfPointActions && printingMode && finishedRender && finishedRender();
  }

  /**
   * get display value for Separator Header
   * @param  {Object} item
   * @param  {any} originalValue
   * @param  {Object} separatorStyle
   * @param  {Object} separatorContent
   */
  getDisplayValueForHeader = (item, originalValue, separatorStyle, separatorContent) => {
    const {
      element,
      element: {properties},
      element: {properties: {fieldItemVisible}},
    } = item;
    const records = separatorContent.data || separatorContent;

    // return blank if this separator is invisible
    if (!fieldItemVisible) {
      return null;
    }

    const value = ReportPartUtils.getDataFormat(
      {
        value: item.value,
        originalValue
      },
      {
        alternativeText: get('dataFormattings.alternativeText', properties),
        fieldFormatData: get('dataFormattings.format.format', properties),
        fieldDataType: get('previewDataType', element)
      },
      {
        element: {
          columnName: item.columnName,
          reportPartElm: element
        },
        record: records[0]
      }
    );

    return separatorStyle.withLabel
      ? String.format('{0}: {1}', get('field.fieldNameAlias', element), value)
      : value;
  };

  render() {
    const {props} = this;
    const {item, separatorContent, reportPart} = props;
    const rawRecord = separatorContent.data;
    const {originalValue} = ReportPartUtils.getFieldValue(item.columnName, head(rawRecord));
    const displayValue = this.getDisplayValueForHeader(
      item,
      originalValue,
      props.separatorStyle,
      separatorContent);

    const dataFormatStyles = ReportPartGridStylingUtil.getDataFormattings(
      item.element,
      !isNil(originalValue) ? originalValue : item.value,
      rawRecord,
      reportPart);

    const fieldElement = item.element;
    const numberOfPointActions = get('numberOfPointActions', fieldElement);

    let htmlDisplayValue = displayValue;
    let displayValueTitle = displayValue;
    let displayValueKey = displayValue;
    if(AppGlobal.isRenderHtmlInReportViewer()){
      htmlDisplayValue = (<span dangerouslySetInnerHTML={{__html: htmlDisplayValue}}></span>);
      displayValueTitle = ReportPartUtils.getTextContentFromHtmlString(displayValueTitle);
      displayValueKey = displayValueTitle;
    }

    if (numberOfPointActions > 0) {
      return (
        <SubReportLink
          reportPart={reportPart}
          fieldElement={fieldElement}
          rawRecord={rawRecord}
          afterRenderCallBack={props.afterRenderCallBack}
          printingMode={props.printingMode}
          containerWidth={get('properties.dataFormattings.width.value', fieldElement)}
          getFilterInfoForSubReport={props.getFilterInfoForSubReport}
          finishedRender={props.finishedRender}
          ignoreCache={props.ignoreCache}
          linkTitleText={displayValueTitle}
        >
              <span key={displayValueKey} style={dataFormatStyles}>
                {htmlDisplayValue}
              </span>
        </SubReportLink>
      );
    }

    return (
      <span key={displayValueKey} style={dataFormatStyles} onClick={props.toggleCollapse}>
            {htmlDisplayValue}
          </span>
    );
  }
}

@observer
class SeparatorHeader extends Component {
  componentWillMount() {
    this.renderListeners = [];
  }

  componentDidMount() {
    const props = this.props;

    props.printingMode &&
    PrintingUtils.publishFinishedRender(this.renderListeners, function () {
      props.finishedRender && props.finishedRender();
    });
  }

  /**
   * get separator sign (,) for each separator
   * @return {String} sign
   */
  render() {
    const {props, renderListeners} = this;
    const {separatorStyle, keyCollection, separatorContent, reportPart} = props;

    let prevItem;
    const getSeparatorSign = (index, item) => {
      const isPrevItemVisible = prevItem && prevItem.element.properties.fieldItemVisible;
      prevItem = item;
      return isPrevItemVisible ? ', ' : '';
    };

    if (separatorStyle.multiLine) {
      return (
        <div className="header-group separator-title u_fullWidth">
          {map(item => {
            const keyString = JSON.stringify(item);
            const separatorHeaderKey = `SeparatorHeader.${keyString}`;

            PrintingUtils.pushRenderListener(renderListeners, separatorHeaderKey);

            return (
              <div>
                <SeparatorHeaderItem
                  item={item}
                  separatorStyle={separatorStyle}
                  separatorContent={separatorContent}
                  reportPart={reportPart}
                  toggleCollapse={props.toggleCollapse}
                  afterRenderCallBack={props.printingMode && props.afterRenderCallBack}
                  printingMode={props.printingMode}
                  getFilterInfoForSubReport={props.getFilterInfoForSubReport}
                  finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, separatorHeaderKey)}
                />
              </div>
            );
          }, keyCollection)}
        </div>
      );
    }
    return (
      <div className="inline-block header-group separator-title u_fullWidth">
        {keyCollection.map((item, index) => {
          const keyString = JSON.stringify(item);
          const separatorHeaderKey = `SeparatorHeader.${keyString}`;

          PrintingUtils.pushRenderListener(renderListeners, separatorHeaderKey);

          return (
            <span key={index}>
                  {getSeparatorSign(index, item)}
              <SeparatorHeaderItem
                item={item}
                separatorStyle={separatorStyle}
                separatorContent={separatorContent}
                reportPart={reportPart}
                toggleCollapse={props.toggleCollapse}
                afterRenderCallBack={props.printingMode && props.afterRenderCallBack}
                printingMode={props.printingMode}
                getFilterInfoForSubReport={props.getFilterInfoForSubReport}
                finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, separatorHeaderKey)}
              />
                </span>
          );
        })}
      </div>
    );
  }
}

@observer
export default class ReportPartGridSeparator extends Component {

  constructor(props) {
    super(props);
    const {reportPart} = this.props;
    this.state = ReportPartGridSeparator.createSeparatorList(reportPart);
  }


  static createSeparatorList =
    ({previewDataFieldsMapping, reportPartContent: {separators}}) => ({
      separatorList: ProcessDataUtils.filterMappingByContainer(
        previewDataFieldsMapping,
        separators)
    });

  componentWillReceiveProps(nextProps) {
    const {props} = this;
    const {records} = props;
    if (records !== nextProps.records) {
      this.setState(ReportPartGridSeparator.createSeparatorList(nextProps.reportPart));
    }
  }

  componentWillMount() {
    this.renderListeners = []; // for cells
  }

  componentDidMount() {
    const {props} = this;

    props.printingMode &&
    PrintingUtils.publishFinishedRender(this.renderListeners, function () {
      props.finishedRender && props.finishedRender();
    });
  }

  componentDidUpdate() {
    const {props: {reportPart: {reportPartContent}}} = this;
    // Reset default collapse
    reportPartContent.setCollapseStatus(COLLAPSE_STATUS.DEFAULT);
  }

  render() {
    const {props} = this;
    const {records, reportPart: {reportPartContent: {separatorStyle}}} = props;
    const separatorList = sortBy('element.position', this.state.separatorList);
    const isLogicalSeparator = separatorStyle.value === ReportPartGridConstants.SEPARATOR_STYLE.LOGICAL.value;

    let convertData = ProcessDataUtils.splitDataForSeparator(records, separatorList, separatorStyle.multiLevel);
    if (props.isPreview) {
      convertData = take(ReportPartGridConstants.NUMBER_OF_PREVIEW_SEPARATOR, convertData);
    }

    if (isLogicalSeparator) {
        return props.printingMode
        ? this.renderLogicalPrintingMode(convertData, separatorStyle)
        : this.renderLogicalNormalMode(convertData, separatorStyle)
    }

    return props.printingMode
      ? this.renderPrintingMode(convertData, separatorStyle)
      : this.renderNormalMode(convertData, separatorStyle);
  }

  expandAll() {
    const {props} = this;
    const reportPartContent = props.reportPart.reportPartContent;

    // isExpandCollapseAllClick used in creating the separator info.
    props.reportPart.isExpandCollapseAllClick = true;

    reportPartContent.setCollapseStatus(COLLAPSE_STATUS.EXPAND);
    this.updateDrillDownRecords(reportPartContent.getDrilldownInfo(true));
  }

  collapseAll() {
    const {props} = this;
    const reportPartContent = props.reportPart.reportPartContent;

    // isExpandCollapseAllClick used in creating the separator info.
    props.reportPart.isExpandCollapseAllClick = true;

    reportPartContent.setCollapseStatus(COLLAPSE_STATUS.COLLAPSE);
    this.updateDrillDownRecords(reportPartContent.getDrilldownInfo(false, COLLAPSE_STATUS.DEFAULT));
  }

  updateDrillDownRecords(drillDownOption) {
    const {props} = this;
    const {reportPart, loader} = props;

    loader.startLoading();
    this.props
      .getDrillDownRecords(reportPart, drillDownOption, reportPart.previewData.paging.pageIndex)
      .then(previewData => reportPart.setQueryData(previewData))
      .finally(loader.endLoading.bind(loader));
  }

  /**
   * function for making the state for each separator.
   */
  buildSeparatorData(data, key, keyPath) {
    if (data.subGroup) {
      key.push(data.key);
      forEach(dd => {
        let newKey = [...key];
        this.buildSeparatorData(dd, newKey, keyPath);
      }, data.subGroup);
    } else {
      key.push(data.key);
      keyPath.push(key);
    }
  }

  /**
   * function for making the report info with record and separator info.
   * @param {*} normalizeData
   * @param {*} isExpandCollapseAllClick if true then just copy the isExpand from alreadyReportInfo
   */
  buildReportInfo(normalizeData, isExpandCollapseAllClick = false, alreadyReportInfo, isLogicalSeparator = false) {
    return normalizeData.map((data, index) => {
      let actualValues = data.key.map(data => data.value);
      const isExpandInfo = isExpandCollapseAllClick ? getOr(true, 'separatorInfo.isExpand', alreadyReportInfo[index]) : true;
      return {
        separatorInfo: {
          isExpand: isExpandInfo,
          isLogicalSeparator: isLogicalSeparator,
          keys: data.key,
          subKey: data.subGroup,
          values: actualValues
        },
        records: []
      }
    });
  }

  /**
   * function for normalize the multi level report data.
   * @param {*} convertData
   */
  normalizeMultiLevelData(convertData) {
    return flattenDeep(map(data => {
      let key = [];
      let keyPath = [];
      this.buildSeparatorData(data, key, keyPath);
      return keyPath.map(dd => {
        return {
          data: data.data,
          key: flattenDeep(dd)
        };
      })
    }, convertData));
  }

  /**
   * Render data with separator as single table.
   */
  renderLogicalNormalMode(convertData, separatorStyle) {
    const {props} = this;
    const {reportPart} = props;
    let alreadyReportInfo = getOr([], 'reportPart.reportInfo', props);
    let normalizeData = this.normalizeMultiLevelData(convertData);
    reportPart.reportInfo = this.buildReportInfo(normalizeData, reportPart.isExpandCollapseAllClick, alreadyReportInfo, true);
    return (
      <SeparatorBody
        {...props}
        separatorContent={convertData}
        separatorStyle={separatorStyle}
        isLogicalSeparator={true}
      />
    );
  }

  // call when user expand/collapse all records, change page no and change page preview.
  renderNormalMode(convertData, separatorStyle) {
    const {props} = this;
    const {reportPart, localization, afterToggleCallback} = props;

    let alreadyReportInfo = getOr([], 'reportPart.reportInfo', props);
    let normalizeData = this.normalizeMultiLevelData(convertData);
    reportPart.reportInfo = this.buildReportInfo(normalizeData, reportPart.isExpandCollapseAllClick, alreadyReportInfo);
    // isExpandCollapseAllClick changed to false so that next time its(isExpandCollapseAllClick) depends
    // on only why its(this function) called.
    this.props.reportPart.isExpandCollapseAllClick = false;

    const forDrillDown =
      reportPart.reportPartContent.gridStyle === ReportPartGridConstants.GRID_STYLES.DRILL_DOWN.value;

    const properties = reportPart.getReportPartProperties();
    const disableRepeatHeaders = reportPart.reportPartContent.gridStyle === GRID_STYLES.VERTICAL.value
      && !properties.headers.removeHeaderForViewer && properties.headers.disableRepeatHeaders;

    return (
      <div>
        {/*Expand/Collapse all section*/}
        {forDrillDown && (
          <div>
              <span
                className="icon-plus-circled"
                title={localization.getText('REPORT_PART_GRID_CONSTANTS_LABEL_EXPAND_ALL_TOOLTIP')}
                onClick={this.expandAll}
              />
            <span
              className="icon-minus-circled"
              title={localization.getText('REPORT_PART_GRID_CONSTANTS_LABEL_COLLAPSE_ALL_TOOLTIP')}
              onClick={this.collapseAll}
            />
          </div>
        )}

        {/*Additional table header for "disable repeat header" enabled*/}
        {disableRepeatHeaders && convertData && convertData.length && (
          <div className="separator-body-standalone">
            <SeparatorBody
              {...props}
              separatorContent={convertData[0]}
              separatorStyle={separatorStyle}
              isLogicalSeparator={false}
              isHeaderOnly/>
          </div>
        )}

        {/*Separator with content*/}
        <CollapsibleList
          defaultExpand
          className="panel separator-grid"
          autoCollapse={false}
          afterToggleCallback={afterToggleCallback}>
          {map(item => {
            const key = map('value', item.key).join('_');
            return (
              <CollapsibleList.Item key={key}>
                <CollapsibleList.ItemHeader className="row panel-heading separator-header">
                  <CollapsibleHeader
                    key={key}
                    separatorStyle={separatorStyle}
                    keyCollection={item.key}
                    separatorContent={item}
                    getFilterInfoForSubReport={props.getFilterInfoForSubReport}
                    reportPart={reportPart}
                  />
                </CollapsibleList.ItemHeader>
                <CollapsibleList.ItemBody className="separator-body" data={item.data}>
                  <SeparatorBody
                    {...props}
                    separatorContent={item}
                    separatorStyle={separatorStyle}
                    isLogicalSeparator={false}/>
                </CollapsibleList.ItemBody>
              </CollapsibleList.Item>
            );
          }, convertData)}
        </CollapsibleList>
      </div>
    );
  }

  renderLogicalPrintingMode(convertData, separatorStyle) {
    const {props, renderListeners} = this;
    const restProps = omit(['finishedRender'], props);

    const keyString = JSON.stringify(convertData.length ? convertData[0].key : '');
    const separatorBodyKey = `SeparatorBody.${keyString}`;
    PrintingUtils.pushRenderListener(renderListeners, separatorBodyKey);
    return (
      <SeparatorBody
        {...restProps}
        separatorContent={convertData}
        separatorStyle={separatorStyle}
        staticRender
        finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, separatorBodyKey)}
        isLogicalSeparator={true}
      />
    );
  }

  renderPrintingMode(convertData, separatorStyle) {
    const {props, renderListeners} = this;
    const {reportPart} = props;
    const usePageBreakAfterSeparator = reportPart.reportPartContent.usePageBreakAfterSeparator;
    let index = 0;
    const numOfRows = convertData && convertData.length;

    return (
      <section className="printMode u-bgWhite">
        {map(item => {
          let elements = [];
          const restProps = omit(['finishedRender'], props);
          const keyString = JSON.stringify(item.key);
          const separatorHeaderKey = `SeparatorHeader.${keyString}`;
          const separatorBodyKey = `SeparatorBody.${keyString}`;

          PrintingUtils.pushRenderListener(renderListeners, separatorHeaderKey);
          PrintingUtils.pushRenderListener(renderListeners, separatorBodyKey);

          elements.push(
            <div style={PrintingUtils.getSeparatorContainerStyle(props)}>
              <div className={PrintingUtils.getSeparatorClassName(props)}>
                {usePageBreakAfterSeparator &&
                index === 0 && (
                  <ReportPartTitle
                    mode={REPORT_PART_MODE.PREVIEW}
                    reportPart={reportPart}
                    className="u_marginBottom izenda-reportPartGrid_titl"
                  />
                )}
                <SeparatorHeader
                  separatorStyle={separatorStyle}
                  keyCollection={item.key}
                  separatorContent={item}
                  reportPart={reportPart}
                  printingMode
                  afterRenderCallBack={props.afterRenderCallBack}
                  getFilterInfoForSubReport={props.getFilterInfoForSubReport}
                  finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, separatorHeaderKey)}
                />
                <SeparatorBody
                  {...restProps}
                  separatorContent={item}
                  separatorStyle={separatorStyle}
                  staticRender
                  finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, separatorBodyKey)}
                  isLogicalSeparator={false}
                />
              </div>
            </div>
          );

          index < numOfRows - 1 && usePageBreakAfterSeparator && elements.push(<p className="pageBreak"/>);
          index++;
          return elements;
        }, convertData)}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridSeparator.js